import { Instance, SnapshotIn, SnapshotOut, isAlive, types } from 'mobx-state-tree';
import { IProductModel, ProductModel } from './product';
import { IUserImageModel } from './userImage';
import { ACTION_BAR_FORMATS } from '@photosi/rn-components/product-groups/product-groups-action-bar/ProductGroupsActionBar.constants';
import { TOAST_IDS } from '@photosi/rn-components/toast/Toast.constants';
import i18n from 'src/app/i18n';
import { DYNAMIC_MODAL_VIEWS } from './dynamic-modal-layer/dynamicModalLayer.constants';
import { DYNAMIC_MODAL_GROUPS } from '../toast/toastStore.types';
import { GROUP_CODES } from '../../constants/code.constants';
import { BaseProductsOptionsModel } from './_base/baseProductsOptionsModel';
import { IReactionDisposer, reaction } from 'mobx';
import { isHappyCaseOrPhotoKit, isMultiGroup } from 'src/helpers/group.helper';
import { TEXT_ICON_BUTTON_TYPES } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import { PRODUCT_STATUS } from './product.constants';

export type TGroupModel = typeof GroupModel;
export type GroupModelInstance = Instance<TGroupModel>;
export interface IGroupModelIn extends SnapshotIn<TGroupModel> {}
export interface IGroupModelOut extends SnapshotOut<TGroupModel> {}
export interface IGroupModel extends GroupModelInstance {}

export const GroupModel = BaseProductsOptionsModel.named('GroupModel')
  .props({
    id: types.identifier,
    code: types.enumeration(Object.values(GROUP_CODES)),
    productFamily: types.string,
    productConfigurationIdDefault: types.string,
    groupIdSDK: types.optional(types.string, ''),
    groupInfo: types.optional(types.array(types.string), []),

    maxItems: types.maybe(types.number),
    minItems: types.maybe(types.number),

    products: types.optional(types.array(types.safeReference(ProductModel)), []),

    imageUrl: types.maybe(types.string),
    isPlaceholder: types.optional(types.boolean, false),
  })
  .volatile<{
    isSelected: boolean;
    titleSuffix: string;
    isSelecting: boolean;
  }>(self => ({
    isSelected: false,
    titleSuffix: '',
    isSelecting: false,
  }))
  .views(self => ({
    get visibleProducts(): IProductModel[] {
      return self.products.filter(
        (product): product is IProductModel => !!product && product.status === PRODUCT_STATUS.IDLE,
      );
    },
  }))
  .views(self => ({
    get isEmpty(): boolean {
      return self.products.length === 0;
    },
    get isHappyCaseOrPhotoKit(): boolean {
      return isHappyCaseOrPhotoKit(self.code);
    },
    get isMultiGroup(): boolean {
      return isMultiGroup(self.code);
    },
    get smartTags() {
      const smartTags = new Map<string, Set<string>>();
      const addProductToSmartTag = (smartTag: string, product: IProductModel) => {
        if (smartTags.has(smartTag)) {
          smartTags.get(smartTag)?.add(product.id);
        } else {
          smartTags.set(smartTag, new Set([product.id]));
        }
      };
      // ADD TAG FOR SELECT ALL
      smartTags.set(ACTION_BAR_FORMATS.ALL, new Set(self.visibleProducts.map(p => p.id)));

      self.visibleProducts.forEach(product => {
        // ADD TAG FOR CROPPED IMAGES
        // const cropAlert = product?.userImages.some(userImage => userImage.cropAlert);
        // if (cropAlert) {
        //   addProductToSmartTag(ACTION_BAR_FORMATS.CUT, product!);
        // }

        // ADD DYNAMIC TAGS
        product.smartSelectionTags?.forEach(smartTag => {
          addProductToSmartTag(smartTag, product);
        });
      });
      return smartTags;
    },
    get userImageAreasWithCropAlert() {
      return self.visibleProducts.flatMap(product => product.userImageAreasWithCropAlert);
    },
    get userImagesWithQualityAlert() {
      const userImages: IUserImageModel[] = [];

      self.visibleProducts.forEach(product => {
        if (product.preview.userImageAreas.some(userImageArea => !!userImageArea.userImage?.qualityAlert)) {
          const userImageAreas = product.preview.userImageAreas;
          userImageAreas
            .filter(userImageArea => !!userImageArea.userImage?.qualityAlert)
            .forEach(userImageArea => {
              if (!!userImageArea.userImage) userImages.push(userImageArea.userImage);
            });
        }
      });
      return userImages;
    },
    get totalProducts(): number {
      return self.visibleProducts.reduce((total, product) => total + product.quantity, 0);
    },
    get orderedProducts(): IProductModel[] {
      return self.visibleProducts.slice().sort((a, b) => a.orderIndex.localeCompare(b.orderIndex));
    },
  }))
  .views(self => ({
    get selectedProducts() {
      return self.orderedProducts.filter(product => product.isSelected);
    },
    get hasAnyImageWithCropAlert(): boolean {
      return self.userImageAreasWithCropAlert.length > 0;
    },
    get hasAnyImageWithQualityAlert(): boolean {
      return self.userImagesWithQualityAlert.length > 0;
    },
  }))
  .views(self => ({
    get isMissingMinProducts(): boolean {
      if (!self.isHappyCaseOrPhotoKit || !self.minItems) return false;
      return self.totalProducts < self.minItems;
    },
    get isMissingMaxProducts(): boolean {
      if (!self.isHappyCaseOrPhotoKit || !self.maxItems) return false;
      return self.totalProducts > self.maxItems;
    },
    get currectProductConfigurationId(): string {
      if (self.isHappyCaseOrPhotoKit && self.visibleProducts.length > 0) {
        return self.visibleProducts[0]?.productConfigurationId;
      } else {
        return self.productConfigurationIdDefault;
      }
    },
  }))
  .actions(self => ({
    setIsSelected(isSelected: boolean) {
      self.isSelected = isSelected;
    },
    setTitleSuffix(suffix: string) {
      self.titleSuffix = suffix;
    },
    setIsSelecting(isSelecting: boolean) {
      self.isSelecting = isSelecting;
    },
    addProductToGroup(product: IProductModel) {
      self.products.push(product);
    },
    removeProductFromGroup(product: IProductModel) {
      self.products.remove(product);
    },
    selectAllProducts() {
      self.visibleProducts.forEach(product => product.setIsSelected(true));
    },
    deselectAllProducts() {
      self.visibleProducts.forEach(product => product.setIsSelected(false));
    },
    selectProductsBy(options: { productIds?: Set<string> } = {}) {
      if (options.productIds) {
        self.visibleProducts.forEach(product => {
          product?.setIsSelected(options.productIds?.has(product.id) ?? false);
        });
      }
    },
    checkToastNotifications() {
      if (self.root.data.uploadError) {
        self.root.dynamicModal?.openModal({
          dynamicModalView: DYNAMIC_MODAL_VIEWS.ModalUploadError,
          onPressDiscardChanges: () => {
            self.root.toastStore.removeToast(TOAST_IDS.ErrorUploadingFiles);
            self.root.data.setUploadError(false);
          },
        });
      }
    },
    hideAllToastNotifications() {
      self.root.toastStore.hideToast(TOAST_IDS.MinProductMissing);
      self.root.toastStore.removeToast(TOAST_IDS.CutsCheckAlert);
      self.root.toastStore.removeGroupToast(DYNAMIC_MODAL_GROUPS.QualityAlertToast);
    },
    checkMinMaxProductsAndNotify() {
      if (!isAlive(self)) return;
      if (isHappyCaseOrPhotoKit(self.code)) {
        const thumbnails = self.root.project.activeProject?.selectedThumbnails ?? [];
        const totalItems = self.visibleProducts.length + thumbnails.length;
        const isCompleteMissing = self?.isMissingMinProducts && totalItems >= self?.minItems;
        const isCreatedNewBox = totalItems >= self?.maxItems;
        const toastKey = isCreatedNewBox ? 'created_newbox' : isCompleteMissing ? 'complete-missing' : null;
        toastKey &&
          self.root.toastStore.showHappyCaseOrPhotokitCompletedToast({
            id: TOAST_IDS.HappyCaseOrPhotokitCompleted,
            message: i18n.t(`toast:${toastKey}_${self?.code}`),
            duration: !isCreatedNewBox ? 4000 : 6000,
            ...(isCreatedNewBox && {
              ctaText: i18n.t('toast:show'),
              ctaIconType: TEXT_ICON_BUTTON_TYPES.ONLY_CTA,
              onPress: () => {
                const groups = self.root.project.activeProject?.groups || [];
                const lastGroup = groups[groups.length - 1];
                self.root.project.switchToGroup({
                  groupId: lastGroup?.id,
                  scrollTo: 'top',
                });
                self.root.toastStore.removeToast(TOAST_IDS.HappyCaseOrPhotokitCompleted);
              },
            }),
          });
      }
    },
    checkMinMaxProductsAndNotifyV3(totalProducts: number) {
      if (!isAlive(self)) return;
      if (isHappyCaseOrPhotoKit(self.code)) {
        const totalItems = totalProducts;
        const isCompleteMissing = self?.isMissingMinProducts && totalItems >= self?.minItems;
        const isCreatedNewBox = totalItems >= self?.maxItems;
        const toastKey = isCreatedNewBox ? 'created_newbox' : isCompleteMissing ? 'complete-missing' : null;
        toastKey &&
          self.root.toastStore.showHappyCaseOrPhotokitCompletedToast({
            id: TOAST_IDS.HappyCaseOrPhotokitCompleted,
            message: i18n.t(`toast:${toastKey}_${self?.code}`),
            duration: !isCreatedNewBox ? 4000 : 6000,
            ...(isCreatedNewBox && {
              ctaText: i18n.t('toast:show'),
              ctaIconType: TEXT_ICON_BUTTON_TYPES.ONLY_CTA,
              onPress: () => {
                const groups = self.root.project.activeProject?.groups || [];
                const lastGroup = groups[groups.length - 1];
                self.root.project.switchToGroup({
                  groupId: lastGroup?.id,
                  scrollTo: 'top',
                });
                self.root.toastStore.removeToast(TOAST_IDS.HappyCaseOrPhotokitCompleted);
              },
            }),
          });
      }
    },
  }))
  .actions(self => ({
    selectProductsBySmartTag(smartTag: string) {
      self.selectProductsBy({ productIds: self.smartTags.get(smartTag) });
    },
    syncProductsOptions() {
      self.updateProductsOptionsDebounced(self.products);
    },
  }))
  .actions(self => {
    let reactions: IReactionDisposer[] = [];

    return {
      afterCreate() {
        if (self.isPlaceholder) return;
        // Init reactions
        const productsReaction = reaction(
          () => ({
            products: self.products,
          }),
          newValue => {
            if (__DEV__) console.log(`[GroupModel] (productsReaction) value changed in: `, newValue);
            if (newValue.products) {
              self.awaitSdkProject().then(() => self.updateProductsOptionsDebounced(newValue.products));
            }
          },
          { name: 'productsReaction', fireImmediately: true, delay: 100 },
        );
        reactions.push(productsReaction);

        if (__DEV__) console.log('[GroupModel] reactions ready: ', reactions.length);

        // Clean up all safeReferences in products array before destruction
        self.products.clear();

        if (__DEV__) console.log('[GroupModel] references cleaned before destroy');
      },

      beforeDestroy() {
        // Remove all reactions before destruction
        reactions.forEach(dispose => dispose());
        reactions = [];
        if (__DEV__) console.log('[GroupModel] all reactions removed.');
      },
    };
  });
